import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, ViewChild, ViewChildren } from '@angular/core';
import {
    addRetailerSelection,
    getSelectedRetailers,
    getRetailers,
    getRetailerState,
    RetailerState,
    clearFilters,
    getNextPageRetailers,
    updateCheckboxes,
    getCheckboxState,
    searchRetailers,
    setCheckboxes,
    getCountries,
} from '@emi/shared/state/filters';
import { Paging } from '@emi/shared/constants';
import { Retailer, CheckboxTreeNode, Page } from '@emi/shared/models';
import { select, Store } from '@ngrx/store';
import { Subject, Subscription } from 'rxjs';
import * as Constants from '@emi/shared/constants';

@Component({
    selector: 'via-filter-retailers',
    templateUrl: './filter-retailers.component.html',
    styleUrls: ['./filter-retailers.component.scss'],
})
export class FilterRetailersComponent implements OnInit, OnDestroy {
    @Input()
    public applyClicked: EventEmitter<boolean>;

    @Input()
    public cancelClicked: EventEmitter<boolean>;

    @Input()
    public showApplyButton: boolean;

    @Input()
    public isOverlay: boolean;

    @Output()
    public showApplyButtonChange = new EventEmitter<boolean>();

    @ViewChild('scrollArea')
    public scrollArea: ElementRef;

    @ViewChildren('page')
    public pageElements: QueryList<ElementRef>;

    @ViewChildren('pageAnchor')
    public pageAnchorElements: QueryList<ElementRef>;
    
    private retailersSubscription: Subscription;
    private applyClickedSubscription: Subscription;
    private cancelClickedSubscription: Subscription;
    private checkBoxSubscription: Subscription;
    private stateSubscription: Subscription;
    public retailersTree: Array<CheckboxTreeNode> = [];
    public selectedRetailers: Array<Retailer> = [];
    public stateRetailers: Array<Retailer> = [];
    public loading: boolean;
    public showError = false;
    public checkedArray: Array<CheckboxTreeNode> = [];
    public errorMessage = '';
    public pages: Page[] = [];
    public totalSize: number | undefined;
    public eventsSubject: Subject<CheckboxTreeNode[]> = new Subject<CheckboxTreeNode[]>();
    public hasSearchedByText = false;
    public searchText = '';
    private nextPageId: string | undefined;
    public noResultsMessage = '';
    private isLargeViewport: boolean;

    constructor(private readonly store: Store<RetailerState>, private changeDetector : ChangeDetectorRef) {}

    public onCheckboxStateChange(selectedCheckbox: CheckboxTreeNode): void {
        this.store.dispatch(updateCheckboxes({ checkbox: selectedCheckbox }));
    }

    public onScrollDown(): void {
        if (this.nextPageId) {
            this.store.dispatch(getNextPageRetailers());
        }
    }

    public ngOnInit(): void {
        this.checkBoxSubscription = this.store.pipe(select(getCheckboxState)).subscribe((y) => {
            this.checkedArray = y.checkboxes;
        });

        this.retailersSubscription = this.store.pipe(select(getSelectedRetailers)).subscribe((retailers) => {
            if (retailers.length > 0) {
                if (this.checkedArray.length === 0) {
                    this.store.dispatch(
                        setCheckboxes({
                            checkboxes: retailers.map((retailer) => {
                                return this.buildCheckboxTreeNode(retailer, true);
                            }),
                        })
                    );
                }
                this.selectedRetailers = retailers;
                this.retailersTree = retailers.map((retailers) => {
                    return this.buildCheckboxTreeNode(retailers);
                });
                let nextPage = new Page('', this.retailersTree);
                this.pages.push(nextPage);
                this.eventsSubject.next(nextPage.value);
            }
            this.checkForResults();
        });

        this.stateSubscription = this.store.pipe(select(getRetailerState)).subscribe((state) => {
            this.loading = state.loading;
            this.showError = state.failed;
            this.errorMessage = state.error;
            this.nextPageId = state.metadata?.nextPageId;
            this.totalSize = state.metadata?.totalResults;
            this.pages = [];
            if (this.selectedRetailers.length === 0) {
                this.retailersTree = state.retailers.map((retailer) => {
                    return this.buildCheckboxTreeNode(retailer);
                });
            }
            this.eventsSubject.next(this.retailersTree);
            while (this.retailersTree.length > 0) {
                let newPage = this.retailersTree.splice(0, Paging.PAGE_SIZE);
                this.pages.push(new Page(this.nextPageId, newPage));
            }
            this.stateRetailers = state.retailers;
            this.checkForResults();

            if (!this.isLargeViewport || this.pages.length > Constants.Paging.LARGE_VIEWPORT_PAGE_THRESHOLD) {
                this.scrollToLastPage();
            }
        });

        this.applyClickedSubscription = this.applyClicked.subscribe(() => {
            const retailers: Array<Retailer> = this.checkedArray
                .map((retailer) => new Retailer(retailer.id, retailer.label))
                .sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1));
            this.store.dispatch(addRetailerSelection({ retailers }));
            this.store.dispatch(getCountries());
        });

        if (this.isOverlay) {
            this.cancelClickedSubscription = this.cancelClicked.subscribe(() => {
                this.pages = [];
                this.store.dispatch(
                    setCheckboxes({
                        checkboxes: this.selectedRetailers.map((retailerType) => {
                            return this.buildCheckboxTreeNode(retailerType, true);
                        }),
                    })
                );
                const currentSelectableRetailerTypes =
                    this.selectedRetailers.length > 0 ? this.selectedRetailers : this.stateRetailers;
                this.retailersTree = currentSelectableRetailerTypes.map((retailerType) => {
                    return this.buildCheckboxTreeNode(retailerType, this.selectedRetailers.length > 0);
                });
                this.eventsSubject.next(this.retailersTree);
                while (this.retailersTree.length > 0) {
                    const newPage = this.retailersTree.splice(0, Paging.PAGE_SIZE);
                    this.pages.push(new Page(this.nextPageId, newPage));
                }
            });
        }

        if (this.selectedRetailers.length === 0 && this.stateRetailers.length === 0) {
            this.store.dispatch(getRetailers());
        }
    }

    public buildCheckboxTreeNode(retailer: Retailer, completed?: boolean): CheckboxTreeNode {
        if (completed) {
            return new CheckboxTreeNode(retailer.id, retailer.name, 'checked');
        }
        const existingRetailer = this.checkedArray.find((x) => x.id === retailer.id);
        return new CheckboxTreeNode(retailer.id, retailer.name, existingRetailer ? 'checked' : 'unchecked');
    }

    public selectionChanged(): void {
        this.showApplyButtonChange.emit(this.selectedRetailers.length !== this.checkedArray.length && this.checkedArray.length > 0);
    }

    public searchFiltersByText(text: string): void {
        this.hasSearchedByText = true;
        this.searchText = text;
        this.store.dispatch(searchRetailers({ searchText: text, delay: Constants.GlobalVariables.FILTER_SEARCH_DELAY }));
    }

    public resetSearch(): void {
        this.hasSearchedByText = false;
        this.searchText = '';
        this.store.dispatch(searchRetailers({ searchText: '', delay: 0 }));
    }

    public checkForResults(): void{
      if(!this.loading && this.stateRetailers.length == 0 && !this.hasSearchedByText && this.selectedRetailers.length == 0){
        this.noResultsMessage = Constants.GlobalVariables.ZERO_SKUS;
        return;
      }
      if(!this.loading && this.stateRetailers.length == 0 && this.hasSearchedByText && this.selectedRetailers.length == 0){
        this.noResultsMessage = Constants.GlobalVariables.NO_SEARCH_RESULTS;
        return;
      }
      this.noResultsMessage = '';
    }

    public scrollToLastPage(): void {
        if (this.pageAnchorElements && this.pageAnchorElements.last && this.pageAnchorElements.last.nativeElement) {
            // TODO: investigate alternative to setTimeout
            setTimeout(() => {
                if (!this.isLargeViewport) {
                    this.pageAnchorElements.last.nativeElement.scrollIntoView();
                } else {
                    const pageAnchorIndex = this.pageAnchorElements.length - Constants.Paging.LARGE_VIEWPORT_PAGE_THRESHOLD;
                    this.pageAnchorElements.get(pageAnchorIndex)?.nativeElement.scrollIntoView();
                }
            }, 0);
        }
    }

    public ngOnDestroy(): void {
        if (this.hasSearchedByText && this.selectedRetailers.length === 0) {
            this.store.dispatch(searchRetailers({ searchText: '', delay: 0 }));
        }
        this.checkBoxSubscription.unsubscribe();
        this.stateSubscription.unsubscribe();
        this.retailersSubscription.unsubscribe();
        this.applyClickedSubscription.unsubscribe();
        if (this.isOverlay) {
            this.cancelClickedSubscription.unsubscribe();
        }
        this.showApplyButtonChange.emit(false);
    }

    public checkScrollArea(): void {
        this.changeDetector.detectChanges();

        const pageHeight = this.pageElements.first.nativeElement.clientHeight;
        const scrollAreaHeight = this.scrollArea.nativeElement.clientHeight;
        const scrollDistance = this.scrollArea.nativeElement.scrollHeight;

        if (pageHeight <= scrollAreaHeight && this.totalSize && this.totalSize > Paging.PAGE_SIZE) {
            this.isLargeViewport = true;

            if (scrollDistance <= scrollAreaHeight) { // Load another page only if there is no scrollbar
                this.onScrollDown();
            }
        }
    }
}
